import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    X, Calendar, ChevronLeft, ChevronRight, Search, RefreshCw, Receipt,
    ExternalLink, AlertCircle, Loader2, Download, Euro, FileText, CheckCircle2,
    AlertTriangle, RotateCcw, Filter
} from 'lucide-react';
import './DailyInvoicesModal.css';
import API_BASE_URL from '../../config/api';

const getTodayStr = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatDatePt = (dateStr) => {
    if (!dateStr) return '';
    try {
        const [y, m, d] = dateStr.split('-').map(Number);
        const dt = new Date(y, m - 1, d);
        return dt.toLocaleDateString('pt-PT', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    } catch {
        return dateStr;
    }
};

const DailyInvoicesModal = ({ isOpen, onClose }) => {
    const [selectedDate, setSelectedDate] = useState(getTodayStr());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState({
        date: getTodayStr(),
        totalCount: 0,
        canceledCount: 0,
        totalGross: 0,
        totalNet: 0,
        documents: []
    });

    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [downloadingId, setDownloadingId] = useState(null);

    const fetchInvoices = useCallback(async (dateToFetch) => {
        if (!dateToFetch) return;
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/api/payment/admin/moloni/invoices-by-day?date=${dateToFetch}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.message || 'Não foi possível carregar os documentos do Moloni.');
            }

            const result = await res.json();
            setData(result);
        } catch (err) {
            console.error('Error fetching Moloni invoices:', err);
            setError(err.message || 'Ocorreu um erro ao carregar as faturas.');
            setData(prev => ({ ...prev, documents: [], totalCount: 0, totalGross: 0, totalNet: 0 }));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            fetchInvoices(selectedDate);
        }
    }, [isOpen, selectedDate, fetchInvoices]);

    const changeDay = (offsetDays) => {
        const [y, m, d] = selectedDate.split('-').map(Number);
        const dt = new Date(y, m - 1, d);
        dt.setDate(dt.getDate() + offsetDays);
        const year = dt.getFullYear();
        const month = String(dt.getMonth() + 1).padStart(2, '0');
        const day = String(dt.getDate()).padStart(2, '0');
        setSelectedDate(`${year}-${month}-${day}`);
    };

    const setToday = () => setSelectedDate(getTodayStr());

    const setYesterday = () => {
        const now = new Date();
        now.setDate(now.getDate() - 1);
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        setSelectedDate(`${year}-${month}-${day}`);
    };

    const handleViewPdf = async (docId, status) => {
        if (status?.toLowerCase() === 'rascunho') {
            alert('Não é possível descarregar o PDF de um documento em rascunho.');
            return;
        }

        setDownloadingId(docId);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/api/payment/admin/moloni/pdf-link/${docId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                const resData = await res.json();
                if (resData.url) {
                    window.open(resData.url, '_blank');
                } else {
                    alert('Link do PDF não disponível no Moloni.');
                }
            } else {
                alert('Erro ao obter link do PDF no Moloni.');
            }
        } catch (err) {
            console.error('Error getting PDF link:', err);
            alert('Ocorreu um erro ao tentar visualizar o PDF.');
        } finally {
            setDownloadingId(null);
        }
    };

    // Extract available document types dynamically
    const availableTypes = useMemo(() => {
        const types = new Set();
        (data.documents || []).forEach(d => {
            if (d.documentType) types.add(d.documentType);
        });
        return Array.from(types);
    }, [data.documents]);

    // Filtered documents
    const filteredDocuments = useMemo(() => {
        return (data.documents || []).filter(doc => {
            // Search filter
            if (searchTerm) {
                const term = searchTerm.toLowerCase();
                const matchNum = doc.number?.toLowerCase().includes(term);
                const matchSeries = doc.documentSetName?.toLowerCase().includes(term);
                const matchName = doc.entityName?.toLowerCase().includes(term);
                const matchVat = doc.entityVat?.toLowerCase().includes(term);
                const matchNotes = doc.notes?.toLowerCase().includes(term);
                if (!matchNum && !matchSeries && !matchName && !matchVat && !matchNotes) return false;
            }

            // Status filter
            if (statusFilter !== 'all' && doc.status !== statusFilter) {
                return false;
            }

            // Type filter
            if (typeFilter !== 'all' && doc.documentType !== typeFilter) {
                return false;
            }

            return true;
        });
    }, [data.documents, searchTerm, statusFilter, typeFilter]);

    if (!isOpen) return null;

    const isCurrentDayToday = selectedDate === getTodayStr();

    return (
        <div className="daily-invoices-modal-overlay" onClick={onClose}>
            <div className="daily-invoices-modal-container" onClick={(e) => e.stopPropagation()}>
                {/* ── Modal Header ── */}
                <div className="daily-invoices-header">
                    <div className="daily-invoices-header-left">
                        <div className="daily-invoices-icon-wrapper">
                            <Receipt size={24} />
                        </div>
                        <div>
                            <h2>Faturas por Dia</h2>
                            <p className="daily-invoices-subtitle">
                                Consulta e auditoria direta de faturas e documentos fiscais emitidos
                            </p>
                        </div>
                    </div>
                    <button className="daily-invoices-close-btn" onClick={onClose} title="Fechar">
                        <X size={20} />
                    </button>
                </div>

                {/* ── Date Navigation Bar ── */}
                <div className="daily-invoices-nav-bar">
                    <div className="date-controls-group">
                        <button
                            type="button"
                            className="date-nav-arrow-btn"
                            onClick={() => changeDay(-1)}
                            title="Dia Anterior"
                        >
                            <ChevronLeft size={18} />
                        </button>

                        <div className="date-input-wrapper">
                            <Calendar size={18} className="calendar-icon" />
                            <input
                                type="date"
                                className="date-picker-input"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                            />
                        </div>

                        <button
                            type="button"
                            className="date-nav-arrow-btn"
                            onClick={() => changeDay(1)}
                            title="Dia Seguinte"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>

                    <div className="date-quick-buttons">
                        <button
                            type="button"
                            className={`quick-day-btn ${isCurrentDayToday ? 'quick-day-btn--active' : ''}`}
                            onClick={setToday}
                        >
                            Hoje
                        </button>
                        <button
                            type="button"
                            className="quick-day-btn"
                            onClick={setYesterday}
                        >
                            Ontem
                        </button>
                        <button
                            type="button"
                            className="refresh-day-btn"
                            onClick={() => fetchInvoices(selectedDate)}
                            disabled={loading}
                            title="Recarregar dados do Moloni"
                        >
                            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                            <span>Atualizar</span>
                        </button>
                    </div>

                    <div className="date-display-badge">
                        <span>{formatDatePt(selectedDate)}</span>
                    </div>
                </div>

                {/* ── Financial Summary KPI Cards ── */}
                <div className="daily-invoices-stats-grid">
                    <div className="kpi-card kpi-card--primary">
                        <div className="kpi-icon-box">
                            <Euro size={20} />
                        </div>
                        <div className="kpi-info">
                            <span className="kpi-label">Total Bruto Faturado</span>
                            <span className="kpi-value">
                                {loading ? '...' : `${(data.totalGross || 0).toFixed(2)}€`}
                            </span>
                        </div>
                    </div>

                    <div className="kpi-card">
                        <div className="kpi-icon-box">
                            <Receipt size={20} />
                        </div>
                        <div className="kpi-info">
                            <span className="kpi-label">Documentos Emitidos</span>
                            <span className="kpi-value">
                                {loading ? '...' : (data.totalCount || 0)}
                            </span>
                        </div>
                    </div>

                    <div className="kpi-card">
                        <div className="kpi-icon-box">
                            <FileText size={20} />
                        </div>
                        <div className="kpi-info">
                            <span className="kpi-label">Total Ilíquido</span>
                            <span className="kpi-value">
                                {loading ? '...' : `${(data.totalNet || 0).toFixed(2)}€`}
                            </span>
                        </div>
                    </div>

                    {data.canceledCount > 0 && (
                        <div className="kpi-card kpi-card--danger">
                            <div className="kpi-icon-box">
                                <RotateCcw size={20} />
                            </div>
                            <div className="kpi-info">
                                <span className="kpi-label">Documentos Anulados</span>
                                <span className="kpi-value">{data.canceledCount}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Filters & Search Row ── */}
                <div className="daily-invoices-filters-row">
                    <div className="search-box-wrapper">
                        <Search size={16} className="search-icon" />
                        <input
                            type="text"
                            placeholder="Pesquisar por cliente, NIF ou documento..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                        {searchTerm && (
                            <button className="clear-search-btn" onClick={() => setSearchTerm('')}>
                                <X size={14} />
                            </button>
                        )}
                    </div>

                    <div className="filters-dropdowns">
                        {availableTypes.length > 1 && (
                            <div className="filter-select-wrapper">
                                <select
                                    value={typeFilter}
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                    className="filter-select"
                                >
                                    <option value="all">Todos os Tipos</option>
                                    {availableTypes.map(t => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className="filter-select-wrapper">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="filter-select"
                            >
                                <option value="all">Todos os Estados</option>
                                <option value="Regularizada">Regularizada / Emitida</option>
                                <option value="Anulada">Anulada</option>
                                <option value="Rascunho">Rascunho</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* ── Content Table / Statuses ── */}
                <div className="daily-invoices-table-container">
                    {loading ? (
                        <div className="daily-invoices-loader-box">
                            <Loader2 size={36} className="animate-spin text-primary" />
                            <p>A comunicar com o Moloni e a carregar faturas...</p>
                        </div>
                    ) : error ? (
                        <div className="daily-invoices-error-box">
                            <AlertCircle size={44} className="text-danger" />
                            <h3>Erro na Comunicação</h3>
                            <p>{error}</p>
                            <button
                                className="retry-btn"
                                onClick={() => fetchInvoices(selectedDate)}
                            >
                                <RefreshCw size={16} /> Tentar Novamente
                            </button>
                        </div>
                    ) : filteredDocuments.length === 0 ? (
                        <div className="daily-invoices-empty-box">
                            <Receipt size={48} className="empty-icon" />
                            <h3>Nenhum documento encontrado</h3>
                            <p>
                                {searchTerm || statusFilter !== 'all' || typeFilter !== 'all'
                                    ? 'Nenhum documento corresponde aos filtros selecionados.'
                                    : `Não foram emitidas faturas no Moloni em ${formatDatePt(selectedDate)}.`}
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* ── Desktop Table View ── */}
                            <div className="daily-invoices-desktop-view">
                                <table className="daily-invoices-table">
                                    <thead>
                                        <tr>
                                            <th>Nº Documento</th>
                                            <th>Série</th>
                                            <th>Tipo</th>
                                            <th>Cliente / Entidade</th>
                                            <th>NIF</th>
                                            <th className="text-right">Ilíquido</th>
                                            <th className="text-right">Total Bruto</th>
                                            <th>Estado</th>
                                            <th className="text-center">PDF</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredDocuments.map((doc) => {
                                            const isCanceled = doc.status?.toLowerCase() === 'anulada';
                                            const isDraft = doc.status?.toLowerCase() === 'rascunho';

                                            let badgeClass = 'status-badge--success';
                                            if (isCanceled) badgeClass = 'status-badge--danger';
                                            else if (isDraft) badgeClass = 'status-badge--warning';

                                            return (
                                                <tr key={doc.documentId} className={`invoice-row ${isCanceled ? 'invoice-row--canceled' : ''}`}>
                                                    <td>
                                                        <div className="doc-number-badge">
                                                            <FileText size={14} />
                                                            <span>{doc.number || `ID #${doc.documentId}`}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="doc-series-badge">
                                                            {doc.documentSetName || '—'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="doc-type-tag">
                                                            {doc.documentType || 'Documento'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="entity-name-cell" title={doc.entityName}>
                                                            {doc.entityName || 'Consumidor Final'}
                                                        </div>
                                                        {doc.notes && (
                                                            <div className="entity-notes-cell" title={doc.notes}>
                                                                {doc.notes}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className="vat-code">
                                                            {doc.entityVat || '999999990'}
                                                        </span>
                                                    </td>
                                                    <td className="text-right font-mono">
                                                        {doc.netValue.toFixed(2)}€
                                                    </td>
                                                    <td className="text-right font-mono font-bold">
                                                        <span className={isCanceled ? 'line-through text-muted' : ''}>
                                                            {doc.grossValue.toFixed(2)}€
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className={`status-badge ${badgeClass}`}>
                                                            {doc.status || 'Regularizada'}
                                                        </span>
                                                    </td>
                                                    <td className="text-center">
                                                        <button
                                                            type="button"
                                                            className="view-doc-pdf-btn"
                                                            onClick={() => handleViewPdf(doc.documentId, doc.status)}
                                                            disabled={downloadingId === doc.documentId || isDraft}
                                                            title={isDraft ? 'Rascunho (PDF indisponível)' : 'Abrir Fatura Oficial (PDF Moloni)'}
                                                        >
                                                            {downloadingId === doc.documentId ? (
                                                                <Loader2 size={16} className="animate-spin" />
                                                            ) : (
                                                                <ExternalLink size={16} />
                                                            )}
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* ── Mobile Card List View ── */}
                            <div className="daily-invoices-mobile-list">
                                {filteredDocuments.map((doc) => {
                                    const isCanceled = doc.status?.toLowerCase() === 'anulada';
                                    const isDraft = doc.status?.toLowerCase() === 'rascunho';

                                    let badgeClass = 'status-badge--success';
                                    if (isCanceled) badgeClass = 'status-badge--danger';
                                    else if (isDraft) badgeClass = 'status-badge--warning';

                                    return (
                                        <div
                                            key={`mob-${doc.documentId}`}
                                            className={`mobile-invoice-card ${isCanceled ? 'mobile-invoice-card--canceled' : ''}`}
                                        >
                                            <div className="mobile-invoice-card-top">
                                                <div className="mobile-invoice-client-info">
                                                    <span className="mobile-invoice-client-name">
                                                        {doc.entityName || 'Consumidor Final'}
                                                    </span>
                                                    <span className="vat-code">
                                                        NIF: {doc.entityVat || '999999990'}
                                                    </span>
                                                </div>
                                                <span className={`status-badge ${badgeClass}`}>
                                                    {doc.status || 'Regularizada'}
                                                </span>
                                            </div>

                                            {doc.notes && (
                                                <div className="mobile-invoice-notes">
                                                    {doc.notes}
                                                </div>
                                            )}

                                            <div className="mobile-invoice-card-meta">
                                                <div className="mobile-invoice-meta-badges">
                                                    <div className="doc-number-badge">
                                                        <FileText size={13} />
                                                        <span>{doc.number || `ID #${doc.documentId}`}</span>
                                                    </div>
                                                    <span className="doc-series-badge">
                                                        {doc.documentSetName || '—'}
                                                    </span>
                                                    <span className="doc-type-tag">
                                                        {doc.documentType || 'Doc'}
                                                    </span>
                                                </div>

                                                <div className="mobile-invoice-financials">
                                                    <div className="mobile-invoice-value-box">
                                                        <span className="mobile-value-label">Total</span>
                                                        <span className={`mobile-gross-value ${isCanceled ? 'line-through text-muted' : ''}`}>
                                                            {doc.grossValue.toFixed(2)}€
                                                        </span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="view-doc-pdf-btn"
                                                        onClick={() => handleViewPdf(doc.documentId, doc.status)}
                                                        disabled={downloadingId === doc.documentId || isDraft}
                                                        title={isDraft ? 'Rascunho (PDF indisponível)' : 'Abrir Fatura Oficial (PDF Moloni)'}
                                                    >
                                                        {downloadingId === doc.documentId ? (
                                                            <Loader2 size={16} className="animate-spin" />
                                                        ) : (
                                                            <ExternalLink size={16} />
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>

                {/* ── Footer ── */}
                <div className="daily-invoices-footer">
                    <div className="footer-count">
                        A mostrar <strong>{filteredDocuments.length}</strong> de <strong>{data.totalCount || 0}</strong> documentos
                    </div>
                    <button type="button" className="btn-close-footer" onClick={onClose}>
                        Fechar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DailyInvoicesModal;
